import { ArrowRight } from "lucide-react";
import { WIDGET_TYPO } from "@/lib/sayz-widget-tokens";

/**
 * Sayz Widget CTA — Sayz Widget config sayfasındaki `ActionRow2Col` rendering
 * path'ini mirror eder.
 *
 * TEMPLATE'LER (2026-03-01):
 *   - minimal (Sade)     → border + light radius + icon + ok
 *   - editorial (Yeni)   → underline link, border/radius YOK, icon YOK,
 *                          sadece metin + ince alt-çizgi. Serif font.
 *   - bold (Yakında)     → disabled
 *
 * Hem FindMySizeWizard içindeki "Beden Tablosunu Gör" CTA'sı hem SizeChartModal
 * içindeki "Bedenimi Bul" cross-link hem WidgetSizingPanel preview'daki ana
 * aksiyonlar — HEPSİ bu component'tan render edilir. Template buradan değişince
 * üçü de senkron şekilde yenilenir.
 *
 * Props:
 *   template  → "minimal" | "editorial" | "bold" (visual branch)
 *   tokens    → { accent, radius, fontScale, spacing, ... }
 *   icon      → Lucide component (editorial'da yoksayılır)
 *   label     → Buton metni
 *   variant   → "primary" | "secondary" (default: "secondary")
 *   onClick   → click handler
 *   testId    → data-testid
 */
const SANS = '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",sans-serif';
const SERIF = 'Georgia,"Times New Roman","Playfair Display",serif';

export const SayzCta = ({
    template = "minimal",
    tokens = {},
    icon: Icon,
    label,
    variant = "secondary",
    onClick,
    testId,
}) => {
    const scale = tokens.fontScale || 1;
    const fs = (px) => `${Math.round(px * scale * 10) / 10}px`;
    const sp = tokens.spacing || 16;
    const accA = tokens.accent || "#C86B4B";
    const radius = tokens.radius ?? 8;
    const isPrimary = variant === "primary";
    const isEditorial = template === "editorial";
    const isBold = template === "bold";

    /* ====================================================================
       BOLD — Field Notes ekolü: filled bg, 1.5px border, chunky weight 700,
       keskin kenar (radius 0), icon kalın stroke 2.25. Primary ve secondary
       benzer "stamp" hissinde.
       ==================================================================== */
    if (isBold) {
        return (
            <button
                type="button"
                onClick={onClick}
                data-testid={testId}
                data-action={testId}
                data-template={template}
                className={`sayz-link sayz-action sayz-action-bold ${isPrimary ? "sayz-action-primary" : "sayz-action-secondary"}`}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    color: isPrimary ? "#fff" : accA,
                    border: `1.5px solid ${accA}`,
                    background: isPrimary ? accA : "transparent",
                    padding: `${sp * 0.6}px ${sp * 1.1}px`,
                    borderRadius: 0,
                    fontSize: fs(WIDGET_TYPO.body),
                    fontWeight: 700,
                    fontFamily: SANS,
                    letterSpacing: "0.02em",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "background-color .15s ease-out, color .15s ease-out",
                }}
            >
                {Icon && (
                    <span
                        className={`sayz-action-icon ${isPrimary ? "sayz-twinkle" : ""}`}
                        data-slot="action-icon"
                        aria-hidden="true"
                        style={{
                            display: "inline-flex",
                            color: isPrimary ? "#fff" : accA,
                            flexShrink: 0,
                            transformOrigin: "center",
                        }}
                    >
                        <Icon size={fs(WIDGET_TYPO.ctaIcon)} strokeWidth={2.25} />
                    </span>
                )}
                <span className="sayz-action-text" data-slot="action-text">
                    {label}
                </span>
                <span
                    className="sayz-action-arrow"
                    data-slot="action-arrow"
                    aria-hidden="true"
                    style={{
                        display: "inline-flex",
                        color: isPrimary ? "#fff" : accA,
                        flexShrink: 0,
                        marginLeft: 2,
                    }}
                >
                    <ArrowRight size={fs(WIDGET_TYPO.ctaIcon)} strokeWidth={2.25} />
                </span>
            </button>
        );
    }

    /* ====================================================================
       EDITORIAL — Underlined link, border/radius YOK, SANS font + hairline icon
       İkon KORUNUR (kullanıcı isteği: her şablonda ikon sabit); sadece
       stroke-width ince, boyut hafifçe küçük — italyan lüks hissi.
       ==================================================================== */
    if (isEditorial) {
        return (
            <button
                type="button"
                onClick={onClick}
                data-testid={testId}
                data-action={testId}
                data-template={template}
                className={`sayz-link sayz-action sayz-action-editorial ${isPrimary ? "sayz-action-primary" : "sayz-action-secondary"}`}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    color: accA,
                    border: "none",
                    background: "transparent",
                    padding: `${sp * 0.35}px 0`,
                    borderRadius: 0,
                    borderBottom: `0.5px solid ${accA}`,
                    fontSize: fs(WIDGET_TYPO.body),
                    fontWeight: isPrimary ? 500 : 400,
                    fontFamily: SANS,
                    letterSpacing: "0.02em",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "opacity .2s ease-out",
                }}
            >
                {Icon && (
                    <span
                        className={`sayz-action-icon ${isPrimary ? "sayz-twinkle" : ""}`}
                        data-slot="action-icon"
                        aria-hidden="true"
                        style={{
                            display: "inline-flex",
                            color: accA,
                            flexShrink: 0,
                            transformOrigin: "center",
                        }}
                    >
                        <Icon size={fs(WIDGET_TYPO.ctaIcon)} strokeWidth={1} />
                    </span>
                )}
                <span className="sayz-action-text" data-slot="action-text">
                    {label}
                </span>
                <span
                    className="sayz-action-arrow"
                    data-slot="action-arrow"
                    aria-hidden="true"
                    style={{
                        display: "inline-flex",
                        color: accA,
                        flexShrink: 0,
                        fontFamily: SANS,
                        lineHeight: 1,
                        fontWeight: 300,
                        marginLeft: 2,
                    }}
                >
                    →
                </span>
            </button>
        );
    }

    /* ====================================================================
       MINIMAL (Sade) — Border + hafif radius + icon + ok
       ==================================================================== */
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            data-action={testId}
            data-template={template}
            className={`sayz-link sayz-action ${isPrimary ? "sayz-action-primary" : "sayz-action-secondary"}`}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: accA,
                border: `0.5px solid ${accA}`,
                background: isPrimary
                    ? `color-mix(in srgb, ${accA} 10%, transparent)`
                    : "transparent",
                // Dikey nefes: sp=12 iken ~8px. Yatay: sp*1.0 (sp=12 → 12px).
                // 2/3 oranı spacing token'ı artarsa (merchant daha ferah
                // isterse) orantılı büyür, ama default ferahlıkta tam 8/12
                // dönüyor — pill-shaped görünümde "squat olmayan" minimal
                // CTA ritmi.
                padding: `${sp * (2 / 3)}px ${sp * 1.0}px`,
                borderRadius: Math.min(radius, 6),
                fontSize: fs(WIDGET_TYPO.body),
                fontWeight: isPrimary ? 500 : 400,
                fontFamily: SANS,
                textDecoration: "none",
                cursor: "pointer",
                transition: "background-color .2s ease-out, opacity .2s ease-out",
            }}
        >
            {Icon && (
                <span
                    className={`sayz-action-icon ${isPrimary ? "sayz-twinkle" : ""}`}
                    data-slot="action-icon"
                    aria-hidden="true"
                    style={{ display: "inline-flex", color: accA, flexShrink: 0, transformOrigin: "center" }}
                >
                    <Icon size={fs(WIDGET_TYPO.ctaIcon)} strokeWidth={1.5} />
                </span>
            )}
            <span className="sayz-action-text" data-slot="action-text">
                {label}
            </span>
            <span
                className="sayz-action-arrow"
                data-slot="action-arrow"
                aria-hidden="true"
                style={{ display: "inline-flex", color: accA, flexShrink: 0, marginLeft: 2 }}
            >
                <ArrowRight size={fs(WIDGET_TYPO.ctaIcon)} strokeWidth={1.5} />
            </span>
        </button>
    );
};
